"use client";

import { ArrowLeftAlt, ArrowRightAlt } from "@ellaandil/ui";
import { clsx } from "@trydig/ui";
import { forwardRef, useCallback, useState } from "react";
import { twMerge } from "tailwind-merge";
import { IconButton } from "@/components/ui/IconButton";
import { Skeleton } from "@/components/ui/Skeleton";
import { Typography } from "@/components/ui/Typography";
import { useTranslations } from "@/i18n/client";

type Props = {
	current?: number;
	total: number;
	onPrev?: () => void;
	onNext?: () => void;
	onChange?: (index: number) => void;
	className?: string;
	darkMode?: boolean;
};

export const Scrollbar = forwardRef<HTMLDivElement, Props>(
	({ current: controlledCurrent, total, onPrev, onNext, onChange, className, darkMode }, ref) => {
		const t = useTranslations();
		const [internalCurrent, setInternalCurrent] = useState(1);
		const isControlled = controlledCurrent !== undefined;
		const current = isControlled ? controlledCurrent : internalCurrent;

		const progressPercent = (current / total) * 100;

		const handlePrev = useCallback(() => {
			if (current <= 1) return;
			const next = current - 1;
			if (!isControlled) setInternalCurrent(next);
			onChange?.(next);
			onPrev?.();
		}, [current, isControlled, onChange, onPrev]);

		const handleNext = useCallback(() => {
			if (current >= total) return;
			const next = current + 1;
			if (!isControlled) setInternalCurrent(next);
			onChange?.(next);
			onNext?.();
		}, [current, total, isControlled, onChange, onNext]);

		return (
			<div ref={ref} className={twMerge("flex items-center gap-2-xs", className)}>
				<Typography
					variant="label-sm-reg"
					as="span"
					className={clsx("shrink-0 leading-[1.15]", darkMode && "text-text-light-text-1")}
				>
					{current}/{total}
				</Typography>

				<div className="flex min-w-px flex-1 flex-col items-start bg-fade-2">
					<div
						className={clsx(
							"h-px bg-shape-dark-fill-1 transition-[width] duration-300 ease-out",
							darkMode && "bg-text-light-text-1",
						)}
						style={{ width: `${progressPercent}%` }}
					/>
				</div>

				<div className="flex shrink-0 items-center">
					<IconButton
						icon={ArrowLeftAlt}
						label={t("Previous")}
						variant="ghost-primary"
						size="sm"
						darkBg={darkMode}
						onClick={handlePrev}
						disabled={current <= 1}
					/>
					<IconButton
						icon={ArrowRightAlt}
						label={t("Next")}
						variant="ghost-primary"
						size="sm"
						darkBg={darkMode}
						onClick={handleNext}
						disabled={current >= total}
					/>
				</div>
			</div>
		);
	},
);

export const ScrollbarSkeleton = () => {
	const t = useTranslations();
	return (
		<div className="flex items-center gap-2-xs">
			<Skeleton fitContent>
				<Typography variant="label-sm-reg" as="span" className="shrink-0 leading-[1.15]">
					1/1
				</Typography>
			</Skeleton>

			<div className="flex min-w-px flex-1 flex-col items-start bg-fade-2">
				<Skeleton className="h-px w-1/3" />
			</div>

			<div className="flex shrink-0 items-center">
				<Skeleton fitContent>
					<IconButton
						icon={ArrowLeftAlt}
						label={t("Previous")}
						variant="ghost-primary"
						size="sm"
						disabled
					/>
				</Skeleton>
				<Skeleton fitContent>
					<IconButton
						icon={ArrowRightAlt}
						label={t("Next")}
						variant="ghost-primary"
						size="sm"
						disabled
					/>
				</Skeleton>
			</div>
		</div>
	);
};

Scrollbar.displayName = "Scrollbar";
